import React, { useRef } from 'react';
import { VideoFeeds } from '../VideoFeeds';
import { VideoControlButtons } from './VideoControlButtons';
import { VideoSlider } from './VideoSlider';
import { FilterType } from '../VideoFilters';

interface DefaultLayoutProps {
  // Video props
  videoSizeRatio: number;
  swapPositions?: boolean;
  triggerResetConnection: boolean;
  setTriggerResetConnection: React.Dispatch<React.SetStateAction<boolean>>;
  sessionActive: boolean;
  handleSessionActive: (sessionActive: boolean) => void;
  selfFilter: string;

  // Slider
  onSliderChange: (value: number) => void;

  // Control buttons
  isPeerAudioMuted: boolean;
  isPeerVideoHidden: boolean;
  togglePeerAudioMute: () => void;
  togglePeerVideoHide: () => void;
  onFilterChange: (filter: FilterType, intensity: number, filterStyle: string) => void;
  onReport?: () => void;
  rtcConnectionState?: string;
}

/**
 * Default side-by-side video layout with slider
 */
export const DefaultLayout: React.FC<DefaultLayoutProps> = ({
  videoSizeRatio,
  swapPositions,
  triggerResetConnection,
  setTriggerResetConnection,
  sessionActive,
  handleSessionActive,
  selfFilter,
  onSliderChange,
  isPeerAudioMuted,
  isPeerVideoHidden,
  togglePeerAudioMute,
  togglePeerVideoHide,
  onFilterChange,
  onReport,
  rtcConnectionState
}) => {
  const videoAreaRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex-1 p-0 flex flex-col overflow-hidden">
      <div className="relative flex-1 flex flex-col overflow-hidden">
        {/* Auto-hiding slider */}
        <VideoSlider
          videoSizeRatio={videoSizeRatio}
          onValueChange={onSliderChange}
          videoAreaRef={videoAreaRef}
        />

        {/* Video container with overlay controls */}
        <div ref={videoAreaRef} className="flex-1 relative overflow-hidden bg-black max-h-full">
          {/* Overlay control buttons */}
          <VideoControlButtons
            isPeerAudioMuted={isPeerAudioMuted}
            isPeerVideoHidden={isPeerVideoHidden}
            togglePeerAudioMute={togglePeerAudioMute}
            togglePeerVideoHide={togglePeerVideoHide}
            onFilterChange={onFilterChange}
            onReport={onReport}
            showReportButton={rtcConnectionState === 'connected'}
          />

          {/* Video feeds */}
          <VideoFeeds
            currentLayout="default"
            swapPositions={swapPositions}
            videoSizeRatio={videoSizeRatio}
            triggerResetConnection={triggerResetConnection}
            setTriggerResetConnection={setTriggerResetConnection}
            sessionActive={sessionActive}
            handleSessionActive={handleSessionActive}
            selfFilter={selfFilter}
          />
        </div>
      </div>
    </div>
  );
};
